import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  TextField,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { PasswordSubmissionMethodForm } from '~/scenes/PeopleModule/components/EmployeeActions/ResetPassword/PasswordSubmissionMethodForm';

import { NewUserAccountDetails } from '../../data';
import { useSxStyles } from '../../useStyles';

import { useNewUserAccount } from './useNewUserAccount';

interface Props {
  dialogTitle: string;
  employeeId: number;
  open: boolean;
  newAccountDetails?: NewUserAccountDetails | undefined;
  showNewPassword?: boolean;
  workEmail: string;
  onClose: () => void;
}

export const NewUserAccountDialog = (props: Props) => {
  const {
    dialogTitle,
    employeeId,
    newAccountDetails,
    showNewPassword,
    open,
    workEmail,
    onClose: onDialogClose,
  } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    form: { control, trigger },
    onSubmit,
    isMethodsAvailable,
    onClose,
  } = useNewUserAccount({
    newAccountDetails: newAccountDetails || {},
    employeeId,
    onClose: onDialogClose,
    workEmail,
  });

  return (
    <DraggableDialog onClose={onClose} open={open}>
      <SolaForceDialogTitle onClose={onClose}>
        {dialogTitle}
      </SolaForceDialogTitle>
      <DialogContent dividers={true}>
        <Box sx={sxStyles.formContent}>
          <Controller
            control={control}
            name={'username'}
            render={({ field: { value, onChange } }) => {
              return (
                <TextField
                  label={t('laLoginUsername')}
                  value={value}
                  onChange={onChange}
                  size={'small'}
                  inputProps={{ readOnly: true }}
                  fullWidth={true}
                />
              );
            }}
          />

          {showNewPassword && (
            <Controller
              control={control}
              name={'password'}
              render={({ field: { value, onChange } }) => {
                return (
                  <TextField
                    label={t('laPassword')}
                    value={value}
                    onChange={onChange}
                    size={'small'}
                    inputProps={{ readOnly: true }}
                    fullWidth={true}
                  />
                );
              }}
            />
          )}
        </Box>
        {isMethodsAvailable && (
          <>
            <Divider sx={sxStyles.divider} />
            <PasswordSubmissionMethodForm
              control={control}
              fieldNames={{
                method: 'passwordSubmit.method',
                workEmail: 'passwordSubmit.workEmail',
                mobile: 'passwordSubmit.mobile',
                alternateEmail: 'passwordSubmit.alternateEmail',
              }}
              triggerRevalidate={trigger}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('close')}</Button>
        <Button onClick={onSubmit}>{t('laSend')}</Button>
      </DialogActions>
    </DraggableDialog>
  );
};
